import type { Session } from "@supabase/supabase-js";
import { apiFetch } from "./client";
import { cart, type CartItem } from "@/features/marketplace/stores/cart";

export interface CartContextItem {
  product_id: string;
  kind: CartItem["origin"];
  quantity: number;
  frequency?: CartItem["frequency"];
  dose_offer_id?: string;
  dose_offer_version?: string;
  lab_purchase_type?: CartItem["lab_purchase_type"];
  lab_interval_months?: CartItem["lab_interval_months"];
}

/** Send identifiers/selections only. Browser prices, titles and payment data stay local. */
export function cartContextItems(items: CartItem[]): CartContextItem[] | null {
  const id = (value: unknown): value is string =>
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 150 &&
    /^[\w.:-]+$/.test(value);
  if (items.length > 30) return null;
  const keys = new Set<string>();
  const result: CartContextItem[] = [];
  for (const item of items) {
    const key = `${item.origin}:${item.id}`;
    if (
      !id(item.id) ||
      !["product", "protocol", "supplement", "lab"].includes(item.origin) ||
      keys.has(key) ||
      !Number.isInteger(item.quantity) ||
      item.quantity < 1 ||
      item.quantity > (item.origin === "supplement" ? 3 : 1)
    )
      return null;
    if (
      item.frequency &&
      !["monthly", "quarterly", "sixMonth", "twelveMonth"].includes(
        item.frequency,
      )
    )
      return null;
    if (
      (item.dose_offer_id && !id(item.dose_offer_id)) ||
      (item.dose_offer_version && !id(item.dose_offer_version))
    )
      return null;
    if (
      item.lab_purchase_type &&
      !["subscription", "one_time"].includes(item.lab_purchase_type)
    )
      return null;
    if (
      item.lab_interval_months &&
      ![1, 3, 6].includes(item.lab_interval_months)
    )
      return null;
    keys.add(key);
    result.push({
      product_id: item.id,
      kind: item.origin,
      quantity: item.quantity,
      ...(item.frequency ? { frequency: item.frequency } : {}),
      ...(item.dose_offer_id ? { dose_offer_id: item.dose_offer_id } : {}),
      ...(item.dose_offer_version
        ? { dose_offer_version: item.dose_offer_version }
        : {}),
      ...(item.lab_purchase_type
        ? { lab_purchase_type: item.lab_purchase_type }
        : {}),
      ...(item.lab_interval_months
        ? { lab_interval_months: item.lab_interval_months }
        : {}),
    });
  }
  return result;
}

type Write = (
  items: CartContextItem[],
  signal: AbortSignal,
) => Promise<unknown>;

/** One bounded write attempt at a time; rapid changes replace the pending snapshot. */
export function createCartContextQueue(write: Write) {
  let pending: { items: CartContextItem[]; revision: number } | undefined;
  let revision = 0;
  let settled = 0;
  let running: Promise<void> | undefined;
  let controller: AbortController | undefined;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const waiters = new Set<{ revision: number; resolve: () => void }>();
  const notify = () => {
    for (const waiter of waiters) {
      if (stopped || settled >= waiter.revision) {
        waiters.delete(waiter);
        waiter.resolve();
      }
    }
  };
  const drain = (): Promise<void> => {
    if (running) return running;
    running = (async () => {
      while (!stopped && pending !== undefined) {
        const snapshot = pending;
        pending = undefined;
        const active = new AbortController();
        controller = active;
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
          await Promise.race([
            Promise.resolve().then(() => write(snapshot.items, active.signal)),
            new Promise<void>((resolve) => {
              timeout = setTimeout(() => {
                active.abort();
                resolve();
              }, 2500);
            }),
          ]);
        } catch {
          /* Support remains available. */
        } finally {
          clearTimeout(timeout);
          settled = snapshot.revision;
          notify();
        }
      }
    })().finally(() => {
      running = undefined;
      if (!stopped && pending !== undefined) void drain();
    });
    return running;
  };
  return {
    enqueue(items: CartContextItem[]) {
      if (stopped) return;
      pending = { items, revision: ++revision };
      clearTimeout(timer);
      timer = setTimeout(() => {
        void drain();
      }, 200);
    },
    async flush() {
      clearTimeout(timer);
      if (stopped || settled >= revision) return;
      // Wait for this snapshot (or a coalesced newer one), not all future edits.
      const complete = new Promise<void>((resolve) => {
        waiters.add({ revision, resolve });
      });
      void drain();
      await complete;
    },
    stop() {
      stopped = true;
      pending = undefined;
      clearTimeout(timer);
      controller?.abort();
      notify();
    },
  };
}

let activeFlush: (() => Promise<void>) | undefined;
let paymentEvent: { owner: string; selection: string; id: string } | undefined;

function selectionKey(items: CartContextItem[]): string {
  return JSON.stringify([...items].sort((a, b) =>
    `${a.kind}:${a.product_id}`.localeCompare(`${b.kind}:${b.product_id}`),
  ));
}

/** Identity/selection only; card edits and browser prices are not new entry. */
export function paymentStepKey(owner: string | null | undefined, items: CartItem[]): string | null {
  const selected = cartContextItems(items);
  return owner && selected?.length ? `${owner}:${selectionKey(selected)}` : null;
}

export type PaymentStepCapture =
  | { status: "disabled" }
  | {
      status: "recorded";
      episode_id: string;
      payment_reached_at: string;
      due_at: string;
      enrollment_eligible: false;
    };

export function startCartContextSync(session: Session | null): () => void {
  if (!session) {
    paymentEvent = undefined;
    return () => {};
  }
  const owner = session.user.id;
  if (paymentEvent && paymentEvent.owner !== owner) paymentEvent = undefined;
  const queue = createCartContextQueue((items, signal) =>
    apiFetch("/me/cart-context", {
      method: "PUT",
      body: JSON.stringify({ items }),
      signal,
      // Never let an account switch replace this captured owner's bearer.
      headers: { Authorization: `Bearer ${session.access_token}` },
    }),
  );
  const observe = () => {
    const snapshot = cart.getSnapshot();
    if (snapshot.ownerId !== owner) return;
    const items = cartContextItems(snapshot.items);
    if (paymentEvent?.owner === owner &&
      (!items?.length || selectionKey(items) !== paymentEvent.selection)) {
      paymentEvent = undefined;
    }
    if (items) queue.enqueue(items);
  };
  const flush = async () => {
    observe();
    await queue.flush();
  };
  activeFlush = flush;
  const unsubscribe = cart.subscribe(observe);
  observe();
  return () => {
    unsubscribe();
    queue.stop();
    if (activeFlush === flush) activeFlush = undefined;
  };
}

export async function flushCartContext(): Promise<void> {
  try {
    await activeFlush?.();
  } catch {
    /* Cart telemetry never blocks support. */
  }
}

/** An explicit visible PAYMENT entry; ordinary cart snapshots never start its clock. */
export async function capturePaymentStep(
  session: Session,
  signal: AbortSignal,
): Promise<PaymentStepCapture | undefined> {
  const owner = session.user.id;
  const snapshot = cart.getSnapshot();
  const items = cartContextItems(snapshot.items);
  if (signal.aborted || snapshot.ownerId !== owner || !items?.length) return;
  const selection = selectionKey(items);
  if (paymentEvent?.owner !== owner || paymentEvent.selection !== selection) {
    paymentEvent = { owner, selection, id: crypto.randomUUID() };
  }
  const event = paymentEvent;
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener("abort", cancel, { once: true });
  const current = () => {
    const latest = cart.getSnapshot();
    const latestItems = cartContextItems(latest.items);
    return !signal.aborted && latest.ownerId === owner &&
      latestItems !== null && latestItems.length > 0 && selectionKey(latestItems) === selection;
  };
  const unsubscribe = cart.subscribe(() => {
    if (!current()) cancel();
  });
  try {
    await flushCartContext();
    if (controller.signal.aborted || !current()) return;
    return await apiFetch<PaymentStepCapture>("/me/cart-context/payment", {
      method: "POST",
      body: JSON.stringify({ event_id: event.id, items }),
      signal: controller.signal,
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
  } finally {
    unsubscribe();
    signal.removeEventListener("abort", cancel);
  }
}
